import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";

import DashboardHeader from "../DashboardHeader";
import FDAResultsList from "./FDAResultsList";
import FDAApplicationDetailPanel from "./FDAApplicationDetailPanel";
import MigrateDropdown from "../MigrateDropdown";
import DownloadDropdown from "../DownloadDropdown";
import GraphComponent from "../../../copilotComponents/GraphComponent";

const FDA_SEARCH_STEPS = [
    { upTo: 10,  label: "Initializing",            icon: "bi-gear",             desc: "Preparing FDA search" },
    { upTo: 40,  label: "Fetching FDA Applications", icon: "bi-search",          desc: "Querying openFDA API across all search fields" },
    { upTo: 60,  label: "Processing Applications",  icon: "bi-cloud-download",   desc: "Storing drug applications to database" },
    { upTo: 80,  label: "Storing Products",         icon: "bi-capsule",          desc: "Saving drug product details" },
    { upTo: 95,  label: "Storing Documents",        icon: "bi-file-earmark-pdf", desc: "Saving approval letters, labels and reviews" },
    { upTo: 100, label: "Saving Results",           icon: "bi-database-check",   desc: "Finalizing search results" },
];

const DiscoveryProgressBar = ({ progress }) => {
    const pct = Math.min(Math.max(progress || 0, 0), 100);
    const activeIdx = FDA_SEARCH_STEPS.findIndex(s => pct <= s.upTo);
    const currentIdx = activeIdx === -1 ? FDA_SEARCH_STEPS.length - 1 : activeIdx;

    return (
        <div className="vh-55 d-flex flex-column justify-content-center p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="discovery-step-icon-circle">
                        <i className={`bi ${FDA_SEARCH_STEPS[currentIdx].icon} ev-fs-14`} style={{ color: "#fff" }} />
                    </div>
                    <div>
                        <p className="ev-reg-p-dark ev-bold-med mb-0">
                            {FDA_SEARCH_STEPS[currentIdx].label}…
                        </p>
                        <p className="ev-small-p-dark text-muted mb-0">FDA search in progress</p>
                    </div>
                </div>
                <span className="ev-bold-med discovery-pct-label">{pct}%</span>
            </div>

            <div className="discovery-progress-track">
                <div
                    className="discovery-progress-fill"
                    style={{
                        width: `${pct}%`,
                        animation: pct < 100 ? "gradient-move 3s linear infinite" : "none",
                    }}
                />
            </div>

            <div className="row g-2">
                {FDA_SEARCH_STEPS.map((s, i) => {
                    const done = pct >= s.upTo;
                    const active = i === currentIdx;
                    const state = done ? "done" : active ? "active" : "pending";
                    return (
                        <div key={i} className="col-4 col-md-2">
                            <div
                                className="ev-card-2 discovery-step-card h-100 d-flex flex-column align-items-start"
                                style={{
                                    border: active ? "1px solid var(--color9)" : "1px solid transparent",
                                    opacity: done || active ? 1 : 0.45,
                                    transition: "opacity 0.4s, border-color 0.4s",
                                }}
                            >
                                <div className={`discovery-step-icon-box ${state}`}>
                                    {done
                                        ? <i className="bi bi-check-lg ev-fs-13" style={{ color: "#fff" }} />
                                        : <i className={`bi ${s.icon} ev-fs-13`} style={{
                                            color: active ? "var(--color1)" : "var(--color17)",
                                        }} />
                                    }
                                </div>
                                <p className="ev-small-p-dark ev-bold-med mb-0">{s.label}</p>
                                <p className="ev-small-p-dark mb-0 mt-1">{s.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const FDA_TABS = [
    { key: "applications", label: "Applications" },
    { key: "time",         label: "Time Graph" },
    { key: "marketing",    label: "Market Status" },
];

const FDADiscoveryDashboard = ({ search, onBack }) => {
    const navigate = useNavigate();

    // ── Status & processing ──
    const [searchProcessingStatus, setSearchProcessingStatus] = useState(
        search?.status || null
    );
    const [isProcessing, setIsProcessing]   = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isMigrating, setIsMigrating]     = useState(false);

    // ── Access control ──
    const [showDiscoveryAccess, setShowDiscoveryAccess] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState(
        (search?.discovery_access || []).map((u) => u.user_id)
    );

    // ── Results ──
    const [results, setResults]               = useState([]);
    const [selectedResult, setSelectedResult] = useState(null);
    const [selectedIds, setSelectedIds]       = useState([]);
    const [selectAll, setSelectAll]           = useState(false);

    // ── Tabs ──
    const [activeTab, setActiveTab] = useState("applications");

    // ── Limit / date ──
    const [limit, setLimit]       = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate]     = useState("");

    // ── Progress ──
    const [progressBar, setProgressBar] = useState(0);

    // ── Graphs ──
    const [timeGraphData, setTimeGraphData] = useState(null);
    const [marketingGraphData, setMarketingGraphData] = useState(null);
    const [graphsLoading, setGraphsLoading] = useState(false);

    // ── Download / Migrate ──
    const [downloadTypes, setDownloadTypes] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedPhase, setSelectedPhase]     = useState({
        id: "ABSTRACT",
        label: "Abstracts Screening Phase",
        shortLabel: "Abstracts Screening",
    });
    const [abstractMigrationStatus, setAbstractMigrationStatus] = useState(null);
    const [fulltextMigrationStatus, setFulltextMigrationStatus] = useState(null);

    // ── Popups ──
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

    const API_BASE_URL = "/pceapi/slr/discovery";

    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    // ── Auto-load on mount ──
    useEffect(() => {
        handleRefresh();
        fetchGraphs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─────────────────────────────────────────────
    //  Handlers

    const handleRunSearch = async () => {
        if (searchProcessingStatus === "RUNNING" || isProcessing) return;

        setIsProcessing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Running FDA search...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/run`,
                {
                    discovery_id: search.id,
                    search_query: search.search_query,
                    search_limit: limit || null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSearchProcessingStatus("RUNNING");
                setPopupSuccessMessage("FDA search started successfully");
                await handleRefresh();
                await fetchGraphs();
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to run search");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Error running search");
        } finally {
            setIsProcessing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const fetchGraphs = async () => {
        if (!search?.id) return;
        setGraphsLoading(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/fda-graphs`,
                { discovery_id: search.id },
                { headers }
            );
            if (res.data?.status === "success") {
                const data = res.data.data;
                setTimeGraphData(data.time_graph ? { figure_dict: data.time_graph } : null);
                setMarketingGraphData(data.marketing_status_graph ? { figure_dict: data.marketing_status_graph } : null);
            }
        } catch (err) {
            console.error("Error fetching FDA graphs:", err);
        } finally {
            setGraphsLoading(false);
        }
        };

    const handleRefresh = async () => {
        setIsProcessing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Loading results...");
        setPopupErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/view-details`,
                { discovery_id: search.id },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                const payload = res.data.data;
                setSearchProcessingStatus(payload.processing_status || null);
                setProgressBar(payload.estimated_completion_at || payload.progress_bar || 0);

                const apps = payload.applications || [];
                const groups = (payload.groups || []).map((g) => ({
                    groupKey:     g.group_label,
                    groupLabel:   g.group_label,
                    applications: g.applications || [],
                }));
                setResults(groups);

                const firstApp = groups[0]?.applications?.[0] || apps[0] || null;
                if (firstApp) {
                    setSelectedResult(firstApp);
                }

                const validIds = new Set(apps.map((r) => r.id));
                setSelectedIds((prev) => prev.filter((id) => validIds.has(id)));
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to fetch results");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Error fetching results");
        } finally {
            setIsProcessing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleSaveAccess = async () => {
        setIsProcessing(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Saving access...");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/save-user-access`,
                {
                    discovery_id: search.id,
                    user_ids: selectedUserIds,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage("Access updated successfully");
                setShowDiscoveryAccess(false);
            } else {
                setPopupErrorMessage(res.data?.message || "Failed to save access");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || err?.message || "Error saving access");
        } finally {
            setIsProcessing(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleDownload = async () => {
        if (selectedIds.length === 0) {
            setPopupErrorMessage("Please select at least one application to download.");
            return;
        }

        setIsDownloading(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage("Downloading...");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/download-fda`,
                {
                    discovery_id: search.id,
                    selected_application_ids: selectedIds,
                    document_types: downloadTypes.length > 0 ? downloadTypes : null,},
                { headers, responseType: "blob" }
            );

            const blob = new Blob([res.data], { type: "application/zip" });
            const url  = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href  = url;
            link.download = `fda_documents_${search.id}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setPopupSuccessMessage(`Downloaded ${selectedIds.length} applications successfully.`);
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || "Failed to download.");
        } finally {
            setIsDownloading(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleMigrate = async ({ project, phase }) => {
        if (!project?.id) {
            setPopupErrorMessage("Please select a project to migrate to.");
            return;
        }

        if (selectedIds.length === 0) {
            setPopupErrorMessage("Please select at least one application to migrate.");
            return;
        }

        const migrationTypeMap = {
            abstract: "abstract",
            ABSTRACT: "abstract",
            full_text: "full_text",
            FULL_TEXT: "full_text",
        };
        const migrationType = migrationTypeMap[phase?.id] || "abstract";

        setIsMigrating(true);
        setIsProcessingPopupVisible(true);
        setProcessingMessage(`Migrating ${selectedIds.length} applications to ${project.name}...`);

        try {
            const res = await axios.post(
                `${API_BASE_URL}/migrate-fda`,
                {
                    discovery_id: search.id,
                    selected_application_ids: selectedIds,
                    fabric_id: project.fabric_id ?? null,
                    thread_id: project.thread_id || null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }

            if (res.data?.status === "success") {
                setPopupSuccessMessage(
                    res.data.message || `Successfully migrated ${selectedIds.length} applications.`
                );
            } else {
                setPopupErrorMessage(res.data?.message || "Migration failed.");
            }
        } catch (err) {
            if (err.response?.data?.status === "NotAuthorized") {
                authNavigate("/login", "iam", navigate);
                return;
            }
            setPopupErrorMessage(err.response?.data?.message || "Failed to migrate.");
        } finally {
            setIsMigrating(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const handleUserSelection = (userId) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = (checked) => {
        setSelectAll(checked);
        setSelectedIds(
            checked
                ? results.flatMap((g) => (g.applications || []).map((a) => a.id))
                : []
        );
    };

    const handleSelectOne = (id, checked) => {
        setSelectedIds((prev) =>
            checked ? [...prev, id] : prev.filter((x) => x !== id)
        );
        if (!checked) setSelectAll(false);
    };

    // ─────────────────────────────────────────────
    //  Render
    // ─────────────────────────────────────────────

    return (
        <div className="mx-1 pb-4">

            {/* ── Top header ── */}
            <DashboardHeader
                searchProcessingStatus={searchProcessingStatus}
                isProcessing={isProcessing}
                showDiscoveryAccess={showDiscoveryAccess}
                setShowDiscoveryAccess={setShowDiscoveryAccess}
                selectedUserIds={selectedUserIds}
                onUserSelection={handleUserSelection}
                onSaveAccess={handleSaveAccess}
                ownerEmail={search?.owner_email || search?.owner || null}
                apiBaseUrl={API_BASE_URL}
                onRunSearch={handleRunSearch}
                onRefresh={handleRefresh}
                onBack={onBack}
                label="Discovery Dashboard"
            />

            {/* ── Limit + Sort by Date ── */}
            <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                <input
                    type="number"
                    min={1}
                    className="formField5 ev-btn-small w-8 ev-bold-med ev-radius-xsmall"
                    placeholder="Limit"
                    value={limit ?? ""}
                    onChange={(e) => {
                        const val = e.target.value;
                        setLimit(val ? Number(val) : null);
                    }}
                />
                <div className="ev-card-2 d-inline-flex align-items-center gap-2 p-1 ev-radius-xsmall">
                    <span className="ev-xsmall-p-dark text-nowrap">Sort by Date</span>
                    <input
                        type="date"
                        className="formField2 ev-btn-small ev-radius-xlarge ev-padding-xsmall2"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                    <span className="ev-xsmall-p-dark">To</span>
                    <input
                        type="date"
                        className="formField2 ev-btn-small ev-radius-xlarge ev-padding-xsmall2"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                </div>
            </div>

            {/* ── Search term card ── */}
            <div className="ev-card-6 mb-3 p-3 ev-radius-xsmall">
                <p className="ev-small-p-dark mb-0">
                    {search?.search_query || "—"}
                </p>
            </div>

            {/* ── Tabs + toolbar ── */}
            <div className="ev-card-4 mx-1 px-3 py-2">
                <div className="d-flex justify-content-between align-items-center gap-1 mb-2">

                    {/* Tabs */}
                    <div className="ev-message-card6 ev-radius-xsmall p-1 d-flex gap-2">
                        {FDA_TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={
                                    activeTab === tab.key
                                        ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med"
                                        : "ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-bold-med"
                                }
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Toolbar */}
                    <div className="d-flex align-items-center flex-shrink-0 gap-2">
                        <DownloadDropdown
                            fdaMode={true}
                            downloadTypes={downloadTypes}
                            onTypesChange={setDownloadTypes}
                            onDownload={handleDownload}
                            disabled={selectedIds.length === 0 || isDownloading}
                            isLoading={isDownloading}
                        />
                        <MigrateDropdown
                            selectedProject={selectedProject}
                            onSelectProject={setSelectedProject}
                            showPhase={false}
                            dropdownAlign="end"
                            onMigrate={handleMigrate}
                            disabled={selectedIds.length === 0 || isMigrating}
                            isLoading={isMigrating}
                            fdaMode={true}
                        />
                    </div>  
                </div>

                {/* ── Tab content ── */}
                <div className="row">

                    {/* Time Graph tab */}
                    {activeTab === "time" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading time graph...</p>
                                </div>
                            ) : timeGraphData ? (
                                <GraphComponent
                                    graphData={timeGraphData}
                                    isLoading={false}
                                    contentId="fda-time"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Time graph not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Marketing Status tab */}
                    {activeTab === "marketing" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading marketing status graph...</p>
                                </div>
                            ) : marketingGraphData ? (
                                <GraphComponent
                                    graphData={marketingGraphData}
                                    isLoading={false}
                                    contentId="fda-marketing"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Marketing status graph not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Applications tab — progress bar when running */}
                    {activeTab === "applications" && searchProcessingStatus === "RUNNING" && (
                        <div className="col-12">
                            <DiscoveryProgressBar progress={progressBar} />
                        </div>
                    )}

                    {/* Applications tab — results */}
                    {activeTab === "applications" && searchProcessingStatus !== "RUNNING" && results.length > 0 && (
                        <>
                            <div className="col-5 pe-1">
                                <FDAResultsList
                                    results={results}
                                    selectedResult={selectedResult}
                                    onSelectResult={setSelectedResult}
                                    selectedIds={selectedIds}
                                    selectAll={selectAll}
                                    onSelectAll={handleSelectAll}
                                    onSelectOne={handleSelectOne}
                                />
                            </div>
                            <div className="col-7 ps-1">
                                <FDAApplicationDetailPanel
                                    application={selectedResult}
                                    allApplications={results.flatMap((g) => g.applications || [])}
                                />
                            </div>
                        </>
                    )}

                    {/* Applications tab — empty state */}
                    {activeTab === "applications" && searchProcessingStatus !== "RUNNING" && results.length === 0 && (
                        <div className="col-12">
                            <div className="d-flex flex-column align-items-center justify-content-center vh-55 py-5">
                                <i className="bi bi-file-earmark-medical ev-fs-50 text-muted mb-3"></i>
                                <p className="ev-reg-p-dark ev-bold-med mb-1">No FDA Applications Found</p>
                                <p className="ev-small-p-dark text-muted text-center mb-0">
                                    Try adjusting your filters or run a new search to find FDA applications.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Analytics tab */}
                    {activeTab === "analytics" && (
                        <div className="col-12">
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <i className="bi bi-bar-chart-line ev-fs-50 text-muted mb-3"></i>
                                <p className="ev-reg-p-dark ev-bold-med mb-1">Analytics Coming Soon</p>
                                <p className="ev-small-p-dark text-muted text-center mb-0">
                                    Approval timeline, market concentration, and dosage spread charts will appear here.
                                </p>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* ── Popups ── */}
            {isProcessingPopupVisible && (
                <ProcessingPopup message={processingMessage || "Processing..."} />
            )}

            {popupErrorMessage && (
                <ErrorPopup
                    message={popupErrorMessage}
                    onClose={() => setPopupErrorMessage("")}
                />
            )}

            {popupSuccessMessage && (
                <SuccessPopup
                    message={popupSuccessMessage}
                    onClose={() => setPopupSuccessMessage("")}
                />
            )}
        </div>
    );
};

export default FDADiscoveryDashboard;